import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Heart, User, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { CartDrawer } from "@/components/CartDrawer";

const NAV = [
  { label: "Shop All", to: "/shop" },
  { label: "Gifts", to: "/shop?category=Gifts" },
  { label: "Toys", to: "/shop?category=Toys" },
  { label: "Decor", to: "/shop?category=Decor" },
  { label: "Stationery", to: "/shop?category=Stationery" },
  { label: "Gift Cards", to: "/gift-cards" },
];

const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const { user, wishlist } = useAuth();
  const { storeOpen } = useStore();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {storeOpen ? (
        <div className="bg-charcoal text-white text-center text-[0.7rem] tracking-[0.2em] uppercase py-2 px-4 font-display">
          Free shipping across Canada on orders over $100 CAD
        </div>
      ) : (
        <div data-testid="store-closed-banner" className="bg-gold text-white text-center text-[0.7rem] tracking-[0.2em] uppercase py-2 px-4 font-display">
          🌱 We're still stocking our shelves — browse freely, checkout opens soon
        </div>
      )}
      <header className="sticky top-0 z-40 bg-page/90 backdrop-blur-md border-b border-soft-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-charcoal"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>

            <nav className="hidden lg:flex items-center gap-7">
              {NAV.slice(0, 5).map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs font-display uppercase tracking-[0.15em] text-charcoal/80 hover:text-gold transition-colors"
                >
                  {n.label}
                </Link>
              ))}
            </nav>

            <Link
              to="/"
              data-testid="nav-logo"
              className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2.5"
            >
              <img
                src="/logo-mark.png"
                alt="LuvSprout heart-sprout logo"
                className="h-11 w-auto"
              />
              <span className="hidden sm:block text-center">
                <span className="block font-serif text-2xl sm:text-3xl text-charcoal leading-none">
                  luv sprout
                </span>
                <span className="eyebrow text-[0.55rem] mt-0.5 block">Boutique &amp; Gifts</span>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link
                to="/gift-cards"
                data-testid="nav-giftcards-link"
                className="hidden lg:block text-xs font-display uppercase tracking-[0.15em] text-charcoal/80 hover:text-gold transition-colors"
              >
                Gift Cards
              </Link>
              <button
                data-testid="nav-account-btn"
                onClick={() => navigate(user ? "/account" : "/login")}
                className="text-charcoal/80 hover:text-gold transition-colors"
                aria-label="Account"
              >
                <User className="h-5 w-5" />
              </button>
              <button
                data-testid="nav-wishlist-btn"
                onClick={() => navigate("/wishlist")}
                className="relative text-charcoal/80 hover:text-gold transition-colors"
                aria-label="Wishlist"
              >
                <Heart className="h-5 w-5" />
                {wishlist.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-gold text-white text-[0.6rem] grid place-items-center">
                    {wishlist.length}
                  </span>
                )}
              </button>
              <button
                data-testid="nav-cart-btn"
                onClick={() => setDrawerOpen(true)}
                className="relative text-charcoal/80 hover:text-gold transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="h-5 w-5" />
                {count > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-gold text-white text-[0.6rem] grid place-items-center"
                  >
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-charcoal/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-page p-6 flex flex-col gap-1">
            <button
              className="self-end text-charcoal mb-4"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X className="h-6 w-6" />
            </button>
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="py-3 font-serif text-lg text-charcoal border-b border-soft-border"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
      <CartDrawer />
    </>
  );
};

const Footer = () => (
  <footer className="mt-24 bg-charcoal text-white/80">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-1">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="LuvSprout logo" className="h-10 w-auto" />
          <span className="font-serif text-2xl text-white">luv sprout</span>
        </div>
        <p className="text-sm mt-3 text-white/60 leading-relaxed">
          A little boutique of thoughtfully gathered gifts, toys, decor &amp; stationery — shipped
          with love across Canada.
        </p>
      </div>
      <div>
        <p className="eyebrow text-white/50 mb-4">Shop</p>
        <ul className="space-y-2 text-sm">
          <li><Link to="/shop" className="hover:text-gold">All Products</Link></li>
          <li><Link to="/shop?category=Gifts" className="hover:text-gold">Gifts</Link></li>
          <li><Link to="/shop?category=Toys" className="hover:text-gold">Toys</Link></li>
          <li><Link to="/gift-cards" className="hover:text-gold">Gift Cards</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow text-white/50 mb-4">Company</p>
        <ul className="space-y-2 text-sm">
          <li><Link to="/about" className="hover:text-gold">About &amp; Contact</Link></li>
          <li><Link to="/account" className="hover:text-gold">My Account</Link></li>
          <li><Link to="/wishlist" className="hover:text-gold">Wishlist</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow text-white/50 mb-4">Say Hello</p>
        <p className="text-sm">info@luvsprout.com</p>
        <p className="text-sm mt-2 text-white/60">Prices in CAD ($).</p>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-white/40">
      © {new Date().getFullYear()} LuvSprout. All rights reserved.
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-page">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
